import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { LanguageProvider } from "./hooks/useLanguage";
import { SubscriptionProvider } from "@/contexts/SubscriptionContext";
import { ChatProvider } from "@/contexts/ChatContext";
import React, { Suspense, lazy, useEffect } from 'react';
import BottomNavigation from '@/components/mobile/BottomNavigation';
import { supabase } from '@/integrations/supabase/client';

// Nouveau système d'authentification standard Supabase
import { AuthProvider } from '@/contexts/AuthContext';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { Skeleton } from "@/components/ui/skeleton";
import ErrorBoundary from '@/components/ErrorBoundary';
import { useReloadProtection } from './hooks/useReloadProtection';
import { CookieConsentBanner } from './components/CookieConsentBanner';
import { PWAInstallPrompt } from './components/PWAInstallPrompt';
import { SubscriptionErrorBoundary } from '@/components/error-boundaries/SubscriptionErrorBoundary';
import { RouteErrorBoundary } from '@/components/error-boundaries/RouteErrorBoundary';
import { AppPrefetch } from '@/components/AppPrefetch';

// 🔥 PERFORMANCE: Preload critical pages (no lazy loading)
import Members from "./pages/Members";
import Networking from "./pages/Networking";
import MemberEvents from './pages/Events';
import Dashboard from './pages/Dashboard';

// Lazy load all other route components for better code splitting
const Index = lazy(() => import("./pages/Index"));
const MemberAccess = lazy(() => import("./pages/MemberAccess"));
const Services = lazy(() => import("./pages/Services"));
const FAQ = lazy(() => import("./pages/FAQ"));
const ClubDashboard = lazy(() => import("./pages/ClubDashboard"));
const ClubManagement = lazy(() => import("./pages/ClubManagement"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AdminMembers = lazy(() => import('@/pages/admin/Members'));
const AdminDashboard = lazy(() => import('@/pages/admin/Dashboard'));
const SystemMonitoring = lazy(() => import('@/pages/admin/SystemMonitoring'));
const Subscriptions = lazy(() => import('@/pages/admin/Subscriptions'));
const Analytics = lazy(() => import('@/pages/admin/Analytics'));
const Events = lazy(() => import('@/pages/admin/Events'));
const Settings = lazy(() => import('@/pages/admin/Settings'));
const Communications = lazy(() => import('@/pages/backoffice/Communications'));
const Notifications = lazy(() => import('@/pages/backoffice/Notifications'));
const BackofficeSettings = lazy(() => import('@/pages/backoffice/Settings'));
const DatabaseManagement = lazy(() => import('@/pages/admin/Database'));
const SecurityCenter = lazy(() => import('@/pages/admin/Security'));
const Reports = lazy(() => import('@/pages/admin/Reports'));
const Workflows = lazy(() => import('@/pages/admin/Workflows'));
const AdminWorkshops = lazy(() => import('@/pages/admin/Workshops'));
const AdminInsights = lazy(() => import('@/pages/admin/Insights'));
const BulkEnrich = lazy(() => import('@/pages/admin/insights/BulkEnrich'));
const AdminOpportunities = lazy(() => import('@/pages/admin/Opportunities'));
const MemberJourney = lazy(() => import('@/pages/MemberJourney'));
const RecruitmentStrategy = lazy(() => import('@/pages/admin/RecruitmentStrategy'));
const AdminNetworking = lazy(() => import('@/pages/admin/Networking'));
const AdminPerks = lazy(() => import('@/pages/admin/Perks'));
const CRM = lazy(() => import('@/pages/admin/CRM'));
const BusinessIntelligence = lazy(() => import('@/pages/admin/BusinessIntelligence'));
const BusinessIntelligenceUnified = lazy(() => import('@/pages/admin/BusinessIntelligenceUnified'));
const Opportunities = lazy(() => import('@/pages/Opportunities'));
const Insights = lazy(() => import('@/pages/Insights'));
const InsightDetail = lazy(() => import('@/pages/InsightDetail'));
const Workshops = lazy(() => import('@/pages/Workshops'));
const MemberRequests = lazy(() => import('@/pages/admin/MemberRequests'));
const Subscription = lazy(() => import('@/pages/Subscription'));
const SubscriptionSuccess = lazy(() => import('@/pages/SubscriptionSuccess'));
const Users = lazy(() => import('@/pages/admin/Users'));
const Profile = lazy(() => import('./pages/Profile'));
const StripeSettings = lazy(() => import('./pages/admin/StripeSettings'));
const AdminInvoices = lazy(() => import('./pages/admin/Invoices'));
const Formation = lazy(() => import('./pages/Formation'));
const Business = lazy(() => import('./pages/Business'));
const Admin = lazy(() => import('./pages/Admin'));
const International = lazy(() => import('./pages/International'));
const Deals = lazy(() => import('./pages/Deals'));
const DealsHub = lazy(() => import('./pages/DealsHub'));
const Perks = lazy(() => import('./pages/Perks'));
// Chat page removed - redirected to /networking?tab=hub
const Commissions = lazy(() => import('./pages/Commissions'));
const CommissionDetail = lazy(() => import('./pages/CommissionDetail'));
const AdminCommissions = lazy(() => import('./pages/admin/Commissions'));
const MemberEnrichment = lazy(() => import('./pages/admin/MemberEnrichment'));
const JoinWithReferral = lazy(() => import('./pages/JoinWithReferral'));
const MemberCard = lazy(() => import('./pages/MemberCard'));
const AdminAnnouncements = lazy(() => import('./pages/admin/Announcements'));
const GDPRSettings = lazy(() => import('./pages/GDPRSettings'));
const Matchmaking = lazy(() => import('./pages/Matchmaking'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const InternationalCompliance = lazy(() => import('./pages/legal/InternationalCompliance'));
const PublicEventLandingPage = lazy(() => import('./pages/PublicEventLandingPage'));
const ComplianceDashboard = lazy(() => import('./pages/admin/ComplianceDashboard'));
const Law1807Dashboard = lazy(() => import('./pages/admin/Law1807Dashboard'));
const DPOContact = lazy(() => import('./pages/DPOContact'));
const FeaturesRoadmap = lazy(() => import('./pages/admin/FeaturesRoadmap'));
const Support = lazy(() => import('./pages/Support'));
const SupportDashboard = lazy(() => import('./pages/admin/SupportDashboard'));
const EmailLogs = lazy(() => import('./pages/admin/EmailLogs'));

// Job Board
const Jobs = lazy(() => import('./pages/Jobs'));
const JobDetails = lazy(() => import('./pages/JobDetails'));
const MyApplications = lazy(() => import('./pages/MyApplications'));
const JobAlerts = lazy(() => import('./pages/JobAlerts'));
const MyJobPosts = lazy(() => import('./pages/MyJobPosts'));
const JobApplicationsReceived = lazy(() => import('./pages/JobApplicationsReceived'));
const PressKit = lazy(() => import('./pages/PressKit'));

// Admin Only
const IdeaBox = lazy(() => import('./pages/IdeaBox'));
const Governance = lazy(() => import('./pages/admin/Governance'));

// Loading component for Suspense fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="space-y-4 w-full max-w-md mx-auto p-4">
      <Skeleton className="h-8 w-3/4 mx-auto" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-5/6" />
      <Skeleton className="h-32 w-full" />
    </div>
  </div>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 15, // 15 minutes (optimisé pour réduire la charge DB)
    },
  },
});

const App = () => {
  // Add reload protection monitoring
  useReloadProtection();
  
  // Handle chunk/module loading errors globally (for errors not caught by ErrorBoundary)
  useEffect(() => {
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const message = event.reason?.message || String(event.reason) || '';
      const isModuleError = 
        message.includes('Failed to fetch dynamically imported module') ||
        message.includes('Importing a module script failed') ||
        message.includes('Loading chunk') ||
        message.includes('ChunkLoadError');
      
      if (isModuleError) {
        console.warn('Module loading failed, clearing cache and reloading...', message);
        
        // Prevent default error handling
        event.preventDefault();
        
        // Check reload frequency protection
        const lastReload = sessionStorage.getItem('lastErrorReload');
        const now = Date.now();
        const timeSinceLastReload = lastReload ? now - parseInt(lastReload) : Infinity;
        
        if (timeSinceLastReload > 10000) {
          sessionStorage.setItem('lastErrorReload', now.toString());
          
          // Clear caches and reload
          if ('caches' in window) {
            caches.keys().then(names => {
              names.forEach(name => caches.delete(name));
            });
          }
          window.location.reload();
        }
      }
    };
    
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    return () => window.removeEventListener('unhandledrejection', handleUnhandledRejection);
  }, []);
  
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <AuthProvider> {/* ✅ AuthProvider global - charge UNE FOIS */}
            <AppPrefetch> {/* ✅ Phase 3: Prefetch intelligent */}
              <BrowserRouter>
                <SubscriptionErrorBoundary>
                  <SubscriptionProvider>
                    <LanguageProvider>
                      <Toaster />
                      <Sonner />
                    <ErrorBoundary>
                      <Suspense fallback={<PageLoader />}>
                      <Routes>
                  <Route path="/" element={
                    <RouteErrorBoundary routeName="Accueil">
                      <Index />
                    </RouteErrorBoundary>
                  } />
                  <Route path="/join/:code" element={<JoinWithReferral />} />
                  <Route path="/event/public/:slug" element={<PublicEventLandingPage />} />
                  <Route path="/member-access" element={<MemberAccess />} />
                  <Route path="/services" element={<Services />} />
                  <Route path="/subscription" element={<Subscription />} />
                  <Route path="/faq" element={<FAQ />} />
                  <Route path="/subscription-success" element={<SubscriptionSuccess />} />
                  <Route path="/profile" element={
                    <RouteErrorBoundary routeName="Profil">
                      <ProtectedRoute>
                        <Profile />
                      </ProtectedRoute>
                    </RouteErrorBoundary>
                  } />
                  <Route path="/profile/card" element={
                    <ProtectedRoute>
                      <MemberCard />
                    </ProtectedRoute>
                  } />
                  <Route path="/members" element={
                    <RouteErrorBoundary routeName="Annuaire">
                      <ProtectedRoute>
                        <Members />
                      </ProtectedRoute>
                    </RouteErrorBoundary>
                  } />
                  <Route path="/networking" element={
                    <ProtectedRoute>
                      <ChatProvider>
                        <Networking />
                      </ChatProvider>
                    </ProtectedRoute>
                  } />
                  <Route path="/events" element={
                    <ProtectedRoute>
                      <MemberEvents />
                    </ProtectedRoute>
                  } />
                  <Route path="/opportunities" element={
                    <RouteErrorBoundary routeName="Opportunités">
                      <ProtectedRoute>
                        <Opportunities />
                      </ProtectedRoute>
                    </RouteErrorBoundary>
                  } />
                  <Route path="/insights" element={
                    <ProtectedRoute>
                      <Insights />
                    </ProtectedRoute>
                  } />
                  <Route path="/insights/:id" element={
                    <ProtectedRoute>
                      <InsightDetail />
                    </ProtectedRoute>
                  } />
                  <Route path="/workshops" element={
                    <ProtectedRoute>
                      <Workshops />
                    </ProtectedRoute>
                  } />
                  <Route path="/commissions" element={
                    <ProtectedRoute>
                      <Commissions />
                    </ProtectedRoute>
                  } />
                  <Route path="/commissions/:id" element={
                    <ProtectedRoute>
                      <CommissionDetail />
                    </ProtectedRoute>
                  } />
                  <Route path="/perks" element={
                    <ProtectedRoute>
                      <Perks />
                    </ProtectedRoute>
                  } />
                   <Route path="/formation" element={
                     <ProtectedRoute>
                       <Formation />
                     </ProtectedRoute>
                   } />
                   <Route path="/business" element={
                     <ProtectedRoute>
                       <Business />
                     </ProtectedRoute>
                   } />
                   <Route path="/deals" element={
                     <ProtectedRoute>
                       <DealsHub />
                     </ProtectedRoute>
                   } />
                   <Route path="/international" element={<International />} />
                   <Route path="/dashboard" element={
                     <RouteErrorBoundary routeName="Tableau de bord">
                       <ProtectedRoute>
                         <Dashboard />
                       </ProtectedRoute>
                     </RouteErrorBoundary>
                   } />
                   <Route path="/club-dashboard" element={
                     <ProtectedRoute>
                       <ClubDashboard />
                     </ProtectedRoute>
                   } />
                   <Route path="/club-management" element={
                     <ProtectedRoute>
                       <ClubManagement />
                     </ProtectedRoute>
                   } />
                   <Route path="/admin" element={
                     <RouteErrorBoundary routeName="Administration">
                       <ProtectedRoute>
                         <Admin />
                       </ProtectedRoute>
                     </RouteErrorBoundary>
                   }>
                     <Route index element={<AdminDashboard />} />
                     <Route path="system-monitoring" element={<SystemMonitoring />} />
                    <Route path="members" element={<AdminMembers />} />
                     <Route path="crm" element={<CRM />} />
                     <Route path="crm-intelligent" element={<Navigate to="/admin/crm" replace />} />
                     <Route path="business-intelligence" element={<BusinessIntelligenceUnified />} />
                     <Route path="automation-workflows" element={<Navigate to="/admin/workflows" replace />} />
                     <Route path="analytics" element={<Navigate to="/admin/business-intelligence" replace />} />
                     <Route path="reports" element={<Navigate to="/admin/business-intelligence" replace />} />
                     <Route path="users" element={<Users />} />
                     <Route path="subscriptions" element={<Subscriptions />} />
                     <Route path="events" element={<Events />} />
                     <Route path="settings" element={<Settings />} />
                     <Route path="member-requests" element={<MemberRequests />} />
                     <Route path="communications" element={<Communications />} />
                     <Route path="email-logs" element={<EmailLogs />} />
                     <Route path="announcements" element={<AdminAnnouncements />} />
                     <Route path="notifications" element={<Notifications />} />
                     <Route path="backoffice-settings" element={<Navigate to="/admin/settings" replace />} />
                    <Route path="workflows" element={<Workflows />} />
                    <Route path="workshops" element={<AdminWorkshops />} />
                     <Route path="perks" element={<AdminPerks />} />
                     <Route path="insights" element={<AdminInsights />} />
                     <Route path="insights/bulk-enrich" element={<BulkEnrich />} />
                     <Route path="opportunities" element={<AdminOpportunities />} />
                     <Route path="networking" element={<AdminNetworking />} />
                     <Route path="recruitment-strategy" element={<RecruitmentStrategy />} />
                     <Route path="commissions" element={<AdminCommissions />} />
                    <Route path="member-enrichment" element={<MemberEnrichment />} />
                    <Route path="database" element={<DatabaseManagement />} />
                    <Route path="security" element={<SecurityCenter />} />
                     <Route path="compliance" element={<ComplianceDashboard />} />
                    <Route path="law-18-07" element={<Law1807Dashboard />} />
                     <Route path="stripe-settings" element={<StripeSettings />} />
                     <Route path="invoices" element={<AdminInvoices />} />
                     <Route path="features" element={<FeaturesRoadmap />} />
                      <Route path="support" element={<SupportDashboard />} />
                      <Route path="governance" element={<Governance />} />
                  </Route>
                  <Route path="/chat" element={<Navigate to="/networking?tab=hub" replace />} />
                  <Route path="/gdpr-settings" element={
                    <ProtectedRoute>
                      <GDPRSettings />
                    </ProtectedRoute>
                  } />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/terms" element={<TermsOfService />} />
                  <Route path="/legal/international-compliance" element={<InternationalCompliance />} />
                  <Route path="/dpo-contact" element={<DPOContact />} />
                  <Route path="/support" element={<Support />} />
                  <Route path="/press-kit" element={<PressKit />} />
                   <Route path="/matchmaking" element={
                    <ProtectedRoute>
                      <Matchmaking />
                    </ProtectedRoute>
                  } />
                  <Route path="/member-journey" element={
                    <ProtectedRoute>
                      <MemberJourney />
                    </ProtectedRoute>
                  } />
                  
                  {/* Job Board Routes */}
                  <Route path="/jobs" element={
                    <RouteErrorBoundary routeName="Job Board">
                      <ProtectedRoute>
                        <Jobs />
                      </ProtectedRoute>
                    </RouteErrorBoundary>
                  } />
                  <Route path="/jobs/alerts" element={
                    <RouteErrorBoundary routeName="Job Alerts">
                      <ProtectedRoute>
                        <JobAlerts />
                      </ProtectedRoute>
                    </RouteErrorBoundary>
                  } />
                  <Route path="/jobs/my-posts" element={
                    <RouteErrorBoundary routeName="Mes offres">
                      <ProtectedRoute>
                        <MyJobPosts />
                      </ProtectedRoute>
                    </RouteErrorBoundary>
                  } />
                  <Route path="/jobs/my-posts/:jobId/applications" element={
                    <RouteErrorBoundary routeName="Candidatures reçues">
                      <ProtectedRoute>
                        <JobApplicationsReceived />
                      </ProtectedRoute>
                    </RouteErrorBoundary>
                  } />
                  <Route path="/jobs/:id" element={
                    <RouteErrorBoundary routeName="Job Details">
                      <ProtectedRoute>
                        <JobDetails />
                      </ProtectedRoute>
                    </RouteErrorBoundary>
                  } />
                  <Route path="/my-applications" element={
                    <RouteErrorBoundary routeName="Mes candidatures">
                      <ProtectedRoute>
                        <MyApplications />
                      </ProtectedRoute>
                    </RouteErrorBoundary>
                  } />
                  
                  {/* Admin Only - Boîte à idées */}
                  <Route path="/idea-box" element={
                    <RouteErrorBoundary routeName="Boîte à idées">
                      <ProtectedRoute>
                        <IdeaBox />
                      </ProtectedRoute>
                    </RouteErrorBoundary>
                  } />
                  
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
              <BottomNavigation />
              <CookieConsentBanner />
              <PWAInstallPrompt />
                  </ErrorBoundary>
                </LanguageProvider>
                      </SubscriptionProvider>
                    </SubscriptionErrorBoundary>
                </BrowserRouter>
              </AppPrefetch>
            </AuthProvider>
          </TooltipProvider>
        </QueryClientProvider>
      </ErrorBoundary>
    );
  };

export default App;
